import { create } from 'zustand'
import { apiClient } from '@/lib/api.ts'

export interface SapPermissions {
  configured: boolean
  orders: boolean
  quotations: boolean
  businessPartners: boolean
  items: boolean
  purchaseOrders: boolean
  purchaseInvoices: boolean
}

interface SapPermissionsState {
  permissions: SapPermissions | null
  loading: boolean
  loaded: boolean
  error: string | null
  fetchPermissions: () => Promise<void>
  refreshPermissions: () => Promise<void>
  reset: () => void
  /** Prueft ob der User fuer ein bestimmtes SAP-Modul berechtigt ist */
  isAllowed: (key: keyof Omit<SapPermissions, 'configured'>) => boolean
}

const EMPTY: SapPermissions = {
  configured: false,
  orders: false,
  quotations: false,
  businessPartners: false,
  items: false,
  purchaseOrders: false,
  purchaseInvoices: false,
}

export const useSapPermissionsStore = create<SapPermissionsState>((set, get) => ({
  permissions: null,
  loading: false,
  loaded: false,
  error: null,

  fetchPermissions: async () => {
    if (get().loading || get().loaded) return
    set({ loading: true, error: null })
    try {
      const { data } = await apiClient.get<SapPermissions>('/sap/me/permissions')
      set({ permissions: data, loaded: true })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler'
      set({ permissions: EMPTY, loaded: true, error: msg })
    } finally {
      set({ loading: false })
    }
  },

  refreshPermissions: async () => {
    set({ loading: true, error: null, loaded: false })
    try {
      const { data } = await apiClient.post<SapPermissions>('/sap/me/permissions/refresh')
      set({ permissions: data, loaded: true })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler'
      set({ permissions: EMPTY, loaded: true, error: msg })
    } finally {
      set({ loading: false })
    }
  },

  reset: () => {
    set({ permissions: null, loading: false, loaded: false, error: null })
  },

  isAllowed: (key) => {
    const p = get().permissions
    if (!p) return true // Not loaded yet → default allow (prevents flash)
    if (!p.configured) return false // No SAP credentials
    return p[key]
  },
}))
